import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from '../../Components/Toast';
import { getCadProjects } from '../../services/cadService';
import { getUserLotDetail } from '../../services/userLotsService';
import { getCadProjectId } from '../../Showroom/utils/cadAccess';
import { getLeadItemTarget } from '../leadsConfig';

/**
 * Click en un interés del lead → navegación de MapPage (`onOpenTarget`).
 *
 * - Lote catastral y lista van directo.
 * - Unidad (`user_lot`): `getuserlotdetail` da el `lot_id`; se emite
 *   `{ type: 'lot', lotId, userLotId }` y MapPage abre la UF directo (sin editar).
 * - Lista CAD / lote CAD: el ítem trae solo `project_id`; el slug y el ACL
 *   salen del catálogo `getcadprojects` (con `owner_user_id` en espacio ajeno).
 *
 * `onOpenTarget` recibe `{ type: 'lot', lotId }`, `{ type: 'list', listId, listName }`
 * o `{ type: 'cad', project, parcelId, parcelLabel }`. Con `fromLead` cada
 * destino lleva además `fromLead: { leadId, leadName }`: MapPage lo usa para
 * el “volver al lead” (ver `buildLeadReturnOrigin`).
 *
 * @param {{ ownerUserId?: string|null, onOpenTarget?: (target: object) => void,
 *   fromLead?: { leadId: string, leadName?: string }|null }} options
 * @returns {{ openItem: (item: object) => void, openingItemId: string, canOpen: boolean }}
 */
export default function useLeadItemOpener({ ownerUserId = null, onOpenTarget, fromLead = null } = {}) {
  const [openingItemId, setOpeningItemId] = useState('');
  const abortRef = useRef(null);
  const callbackRef = useRef(onOpenTarget);
  callbackRef.current = onOpenTarget;
  const fromLeadRef = useRef(fromLead);
  fromLeadRef.current = fromLead;

  const emit = useCallback((target) => {
    const origin = fromLeadRef.current;
    callbackRef.current?.(origin?.leadId ? { ...target, fromLead: origin } : target);
  }, []);

  useEffect(() => () => abortRef.current?.abort(), []);

  const openItem = useCallback(async (item) => {
    const target = getLeadItemTarget(item);
    if (!target || !callbackRef.current) return;
    if (target.type === 'lot' || target.type === 'list') {
      emit(target);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setOpeningItemId(item.id);
    try {
      if (target.type === 'unit') {
        const res = await getUserLotDetail(null, target.userLotId, { ownerUserId });
        if (controller.signal.aborted) return;
        const lotId = String(res?.data?.lot_id || '').trim();
        if (lotId) emit({ type: 'lot', lotId, userLotId: target.userLotId });
        else toast.error('No se encontró esa unidad.');
        return;
      }
      const res = await getCadProjects({ ownerUserId, signal: controller.signal });
      if (controller.signal.aborted) return;
      if (!res?.ok) {
        toast.error(res?.message || 'No se pudo abrir la lista.');
        return;
      }
      const project = (res.projects || []).find((row) => getCadProjectId(row) === target.projectId);
      if (!project?.slug) {
        toast.error('Esa lista ya no está disponible.');
        return;
      }
      emit({
        type: 'cad',
        project,
        parcelId: target.parcelId,
        parcelLabel: target.parcelLabel,
      });
    } catch (err) {
      if (err?.name !== 'AbortError') toast.error('No se pudo abrir.');
    } finally {
      if (abortRef.current === controller) setOpeningItemId('');
    }
  }, [ownerUserId, emit]);

  return { openItem, openingItemId, canOpen: Boolean(onOpenTarget) };
}
